import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Grid,
  CircularProgress
} from '@mui/material';
import { Description as DescriptionIcon } from '@mui/icons-material';
import { Project } from '../../services/projectService';
import { sowService, SoWData } from '../../services/sowService';
import SoWDisplay from '../SoWDisplay';
import SoWEditor from '../SoWEditor';

interface SoWGenerationTabProps {
  project: Project;
  isQuestionnaireComplete?: boolean;
  onMoveToBuilderInvitation?: () => void;
  onProjectUpdate?: () => void;
}

const SoWGenerationTab: React.FC<SoWGenerationTabProps> = ({
  project,
  isQuestionnaireComplete = false,
  onMoveToBuilderInvitation,
  onProjectUpdate
}) => {
  const [editMode, setEditMode] = useState(false);
  const [sow, setSow] = useState<SoWData | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Initialize generating based on project status hint, but verify later
  const [generating, setGenerating] = useState(project.status === 'sow_generation');

  // 1. INITIAL LOAD & STATUS CHECK
  useEffect(() => {
    const checkStatusAndLoad = async () => {
      if (!project.sowId) {
        setLoading(false);
        setGenerating(false);
        return;
      }

      try {
        setLoading(true);
        
        // Check the status of the SoW Task
        const statusResult = await sowService.getSoWStatus(project.id, project.sowId);
        
        if (statusResult.status === 'generating') {
          setGenerating(true); // This will trigger the polling effect
          setLoading(false);   // Stop main loading, show generating UI
        } else if (statusResult.status === 'completed') {
          setGenerating(false);
          // Load the actual document
          const existingSow = await sowService.getSoW(project.id, project.sowId);
          console.log("AmitD: Loaded SoW:", existingSow);
          setSow(existingSow);
          setLoading(false);
        } else if (statusResult.status === 'failed') {
          setGenerating(false);
          setLoading(false);
          // Optional: Set an error state here
        } else {
            // Fallback for legacy or unknown states
            setLoading(false);
        }
      } catch (error) {
        console.error('Failed to load SoW status:', error);
        setLoading(false);
        setGenerating(false);
      }
    };

    checkStatusAndLoad();
  }, [project.id, project.sowId]);

  // 2. POLLING EFFECT (Runs whenever 'generating' is true)
  useEffect(() => {
    if (!generating || !project.sowId) return;

    console.log("Starting SoW Polling...");
    const pollInterval = setInterval(async () => {
      try {
        const statusResult = await sowService.getSoWStatus(project.id, project.sowId!);

        if (statusResult.status === 'completed') {
          clearInterval(pollInterval);
          
          // Fetch the completed SoW
          const completedSow = await sowService.getSoW(project.id, project.sowId!);
          setSow(completedSow);
          setGenerating(false);

          if (onProjectUpdate) {
            onProjectUpdate();
          }
          // alert('Scope of Work generated successfully!'); // Optional: Toast is better
        } else if (statusResult.status === 'failed') {
          clearInterval(pollInterval);
          setGenerating(false);
          alert('Failed to generate Scope of Work. Please try again.');
        }
      } catch (pollError) {
        console.error('Error polling SoW status:', pollError);
      }
    }, 3000);

    // Safety timeout (5 minutes)
    const timeoutId = setTimeout(() => {
      if (generating) {
        clearInterval(pollInterval);
        setGenerating(false);
        alert('SoW generation is taking longer than expected. Please refresh the page.');
      }
    }, 300000);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(timeoutId);
    };
  }, [generating, project.id, project.sowId, onProjectUpdate]);


  const handleAcceptSoW = async () => {
    try {
      setAccepting(true);
      await sowService.acceptSoW(project.id, project.sowId || '');
      if (onMoveToBuilderInvitation) {
        onMoveToBuilderInvitation();
      }
    } catch (error) {
      console.error('Failed to accept SoW:', error);
    } finally {
      setAccepting(false);
    }
  };

  const handleEditSoW = () => setEditMode(true);
  
  const handleCompleteSoW = () => {
    setEditMode(false);
    if (onMoveToBuilderInvitation) onMoveToBuilderInvitation();
  };

  const handleGenerateSoW = async () => {
    try {
      setGenerating(true); // This triggers the polling effect immediately
      const { sowId } = await sowService.generateSoW(project.id);
      
      // If the ID changed (new generation), we might need to update parent or local ref
      // But usually project.sowId needs to be updated via onProjectUpdate if the ID changed
      if (onProjectUpdate) onProjectUpdate();
      
    } catch (error) {
      console.error('Failed to start generation:', error);
      setGenerating(false);
      alert('Failed to start generation. Please try again.');
    }
  };

  // Helper to determine if we are actually ready
  const isReady = isQuestionnaireComplete || 
                  project.status === 'sow_generation' || 
                  project.status === 'sow_ready' ||
                  project.status === 'quotes_received' ||
                  project.status === 'completed';

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Scope of Work Generation
            </Typography>
          </Box>

          {!isReady ? (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Scope of Work can be generated once all the questionnaire phases are completed.
            </Alert>
          ) : project.status === 'sow_ready' || project.status === 'quotes_received' ? (
            <Alert severity="success" sx={{ mb: 3 }}>
              Your Scope of Work is ready! You can now invite builders to quote on your project.
            </Alert>
          ) : !sow && !generating ? (
            <>
              <Typography variant="body1" paragraph>
                Generate a comprehensive Scope of Work document based on your project requirements.
              </Typography>

              <Alert severity="info" sx={{ mb: 3 }}>
                Our AI will create a detailed specification document that builders can use to provide accurate quotes.
              </Alert>

              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGenerateSoW}
                  disabled={loading} // Disable if initial check is still running
                  startIcon={<DescriptionIcon />}
                >
                  Generate Scope of Work
                </Button>
              </Box>
            </>
          ) : null}

          {/* Show Generating State explicitly */}
          {generating && (
             <Box sx={{ textAlign: 'center', py: 4 }}>
               <CircularProgress size={40} sx={{ mb: 2 }} />
               <Typography variant="h6">Generating Scope of Work...</Typography>
               <Typography variant="body2" color="text.secondary">
                 This usually takes about 3-5 minutes. You can return later (if you want) to view the SoW document.
               </Typography>
             </Box>
          )}

        </CardContent>
      </Card>

      {editMode ? (
        <Card>
          <CardContent>
            <SoWEditor
              projectId={project.id}
              sowId={project.sowId || ''}
              onComplete={handleCompleteSoW}
            />
          </CardContent>
        </Card>
      ) : loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Checking Scope of Work status...</Typography>
        </Box>
      ) : (
        <Box>
          {sow && !generating && (
            <>
                <SoWDisplay
                data={sow}
                projectTitle={project.requirements?.description}
//                generatedAt={sow.generatedAt}
                showActions={true}
                />

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Grid container spacing={2} justifyContent="center">
                    <Grid item>
                    <Button
                        variant="outlined"
                        onClick={handleEditSoW}
                        startIcon={<DescriptionIcon />}
                    >
                        Edit SoW
                    </Button>
                    </Grid>
                    <Grid item>
                    <Button
                        variant="contained"
                        onClick={handleCompleteSoW}
                        disabled={!isReady}
                    >
                        Continue to Builder Invitation
                    </Button>
                    </Grid>
                </Grid>
                </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SoWGenerationTab;